import { useCallback, useMemo, useState } from 'react';

/**
 * Manages the browser-style isolation history for FlameChart / FlameGraph.
 *
 * The stack is stored as an ordered list of span ids and a cursor. Isolating
 * from a non-tail cursor discards the forward entries and starts a new branch
 * from the current level — mirroring how a browser's history rewrites forward
 * pages when you follow a new link after going back.
 * @internal
 */
export type FlameIsolationState = {
  /**
   * Id of the currently isolated span, or `undefined` when the full dataset is displayed.
   */
  isolatedId: unknown;
  /**
   * Push a new isolation level. Truncates any forward history first.
   */
  isolate: (id: unknown) => void;
  /**
   * Move one level toward the original root. No-op when already at the root.
   */
  goBack: () => void;
  /**
   * Re-advance one level along the previously taken path. No-op at the tail.
   */
  goForward: () => void;
  canGoBack: boolean;
  canGoForward: boolean;
};

type Stack = { history: unknown[]; index: number };

const INITIAL_STACK: Stack = { history: [], index: -1 };

/**
 * @internal
 */
export function useFlameIsolationState(): FlameIsolationState {
  const [stack, setStack] = useState<Stack>(INITIAL_STACK);

  const isolate = useCallback((id: unknown) => {
    setStack((prev) => {
      const truncated = prev.history.slice(0, prev.index + 1);
      truncated.push(id);
      return { history: truncated, index: prev.index + 1 };
    });
  }, []);

  const goBack = useCallback(() => {
    setStack((prev) =>
      prev.index >= 0 ? { history: prev.history, index: prev.index - 1 } : prev,
    );
  }, []);

  const goForward = useCallback(() => {
    setStack((prev) =>
      prev.index < prev.history.length - 1
        ? { history: prev.history, index: prev.index + 1 }
        : prev,
    );
  }, []);

  return useMemo(
    () => ({
      isolatedId: stack.index >= 0 ? stack.history[stack.index] : undefined,
      isolate,
      goBack,
      goForward,
      canGoBack: stack.index >= 0,
      canGoForward: stack.index < stack.history.length - 1,
    }),
    [stack, isolate, goBack, goForward],
  );
}
